// src/components/EmailVerificationForm.js
import React, { useState, useEffect } from 'react';
import { Mail, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function EmailVerificationForm({ email, onVerificationSuccess, onBackToLogin }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  // Handle countdown for resend button
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleCodeChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;
    
    // Handle pasting entire code in first box
    if (index === 0 && value.length === 6) {
      const digits = value.split('');
      setCode(digits);
      setError('');
      
      // Auto-submit immediately when 6-digit code is pasted
      handleSubmit(value);
      return;
    }
    
    const newCode = [...code];
    newCode[index] = value.slice(-1); // Only take the last character
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    // Auto-submit when all fields are filled
    if (newCode.every(digit => digit) && newCode.join('').length === 6) {
      handleSubmit(newCode.join(''));
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    
    // Check if pasted data is exactly 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setCode(digits);
      setError('');
      
      // Auto-submit immediately
      handleSubmit(pastedData);
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleSubmit = async (codeValue = null) => {
    const verificationCode = codeValue || code.join('');
    
    if (verificationCode.length !== 6) {
      setError('Ange alla 6 siffror');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/auth/verify-email-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          code: verificationCode
        })
      });

      const data = await response.json();

      if (response.ok) {
        if (data.already_verified) {
          setSuccess('E-posten är redan verifierad!');
        } else {
          setSuccess('E-post verifierad! Omdirigerar till inloggning...');
        }
        
        // Call success callback immediately for faster navigation
        setTimeout(() => {
          console.log('📧 EmailVerificationForm: Calling onVerificationSuccess callback');
          
          // Track email verification code success
          trackEvent('Email Verification Code Success', {
            location: 'email-verification-form',
            email: email,
            verification_method: 'manual_code_entry'
          });
          
          onVerificationSuccess(email); // Pass email so parent can handle login
        }, 500);
      } else {
        setError(data.detail || 'Ogiltig verifieringskod');
        // Clear the code inputs on error
        setCode(['', '', '', '', '', '']);
        // Focus first input
        const firstInput = document.getElementById('code-0');
        if (firstInput) firstInput.focus();
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError('Ett fel uppstod. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/auth/resend-verification-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email
        })
      });

      const data = await response.json();

      if (response.ok) {
        if (data.already_verified) {
          setSuccess('E-posten är redan verifierad!');
          setTimeout(() => {
            // Track email already verified
            trackEvent('Email Already Verified', {
              location: 'email-verification-form',
              email: email,
              verification_method: 'already_verified'
            });
            
            onVerificationSuccess(email); // Pass email so parent can handle login
          }, 1500);
        } else {
          setSuccess('Ny verifieringskod skickad till din e-post!');
          setResendCountdown(60); // 60 second cooldown
          // Clear any previous code
          setCode(['', '', '', '', '', '']);
          // Focus first input
          const firstInput = document.getElementById('code-0');
          if (firstInput) firstInput.focus();
        }
      } else {
        setError(data.detail || 'Kunde inte skicka ny kod');
      }
    } catch (err) {
      console.error('Resend error:', err);
      setError('Ett fel uppstod. Försök igen.');
    } finally {
      setResendLoading(false);
    }
  };

  if (success && success.includes('verifierad')) {
    return (
      <div className="max-w-md mx-auto my-8 p-8 border border-gray-300 rounded-lg bg-white shadow-lg">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">E-post verifierad!</h2>
          <p className="text-green-600 mb-6">{success}</p>
          <p className="text-gray-600">Du omdirigeras automatiskt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-24 my-8 p-8 border border-gray-300 rounded-lg bg-white shadow-lg">
      <div className="text-center mb-6">
        <Mail className="w-12 h-12 text-brand-primary mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifiera din e-post</h2>
        <p className="text-gray-600 mb-4">
          Vi har skickat en 6-siffrig kod till
        </p>
        <p className="font-medium text-gray-900 mb-4">{email}</p>
        <p className="text-sm text-gray-500">
          Ange koden nedan för att verifiera din e-postadress
        </p>
      </div>

      {/* 6-digit code input */}
      <div className="mb-6">
        <div className="flex justify-center space-x-2 mb-4">
          {code.map((digit, index) => (
            <input
              key={index}
              id={`code-${index}`}
              type="text"
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : (e) => e.preventDefault()}
              className="w-12 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-brand-primary focus:outline-none transition-colors"
              maxLength={index === 0 ? "6" : "1"}
              autoComplete="off"
            />
          ))}
        </div>

        {/* Auto-submit info */}
        <p className="text-xs text-gray-500 text-center">
          Koden skickas automatiskt när alla siffror är ifyllda<br/>
          <span className="text-gray-400">Du kan också klistra in hela 6-siffriga koden i första rutan</span>
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && !success.includes('verifierad') && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          {success}
        </div>
      )}

      {/* Manual submit button (backup) */}
      <button
        onClick={() => handleSubmit()}
        disabled={loading || code.join('').length !== 6}
        className="w-full bg-brand-primary text-white py-3 px-4 rounded-lg hover:bg-red-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium mb-4 flex items-center justify-center"
      >
        {loading ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
            Verifierar...
          </>
        ) : (
          'Verifiera kod'
        )}
      </button>

      {/* Resend code */}
      <div className="text-center">
        <p className="text-sm text-gray-600 mb-2">Fick du ingen kod?</p>
        <button
          onClick={handleResendCode}
          disabled={resendLoading || resendCountdown > 0}
          className="text-brand-primary hover:text-red-900 underline text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
        >
          {resendLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mr-1"></div>
              Skickar...
            </>
          ) : resendCountdown > 0 ? (
            `Skicka igen om ${resendCountdown}s`
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-1" />
              Skicka ny kod
            </>
          )}
        </button>
      </div>

      {/* Back to login */}
      <div className="text-center mt-6 pt-4 border-t border-gray-200">
        <button
          onClick={onBackToLogin}
          className="text-gray-600 hover:text-gray-800 text-sm"
        >
          ← Tillbaka till inloggning
        </button>
      </div>
    </div>
  );
}

export default EmailVerificationForm;
